
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Layout from '../components/Layout';
import HeroSection from '../components/HeroSection';
import AboutSection from '../components/AboutSection';
import ProductsSection from '../components/ProductsSection';
import IndustriesSection from '../components/IndustriesSection';
import WhyChooseUsSection from '../components/WhyChooseUsSection';
import TestimonialsSection from '../components/TestimonialsSection';
import ContactSection from '../components/ContactSection';
import CTASection from '../components/CTASection';

const Index = () => {
  const location = useLocation();

  useEffect(() => {
    // Handle scroll to contact section if state is provided
    if (location.state?.scrollToContact) {
      const contactSection = document.getElementById('contact');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
      // Clear the state to prevent repeated scrolling
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Updated structured data for organization (JSON-LD)
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    'name': 'TrueCore Gadgets',
    'alternateName': 'Truecore Gadget Innovations',
    'url': 'https://www.truecoregadgets.com',
    'logo': 'https://www.truecoregadgets.com/lovable-uploads/461a823a-5781-4980-ad39-ffec726a88a3.png',
    'description': 'TrueCore Gadgets manufactures custom Android tablets, rugged POS devices, enterprise-grade embedded electronics, and custom PCs tailored for industrial and business applications in India.',
    'foundingDate': '2000',
    'address': {
      '@type': 'PostalAddress',
      'addressLocality': 'Mumbai',
      'addressRegion': 'MH',
      'postalCode': '400069',
      'addressCountry': 'IN'
    },
    'contactPoint': {
      '@type': 'ContactPoint',
      'telephone': '022 6964 7168',
      'contactType': 'customer service',
      'email': 'support@truecoregadgets.com'
    },
    'sameAs': [
      'https://www.facebook.com/truecoregadgets',
      'https://www.instagram.com/truecoregadgets',
      'https://www.linkedin.com/company/truecoregadgets'
    ]
  };

  // Structured data for products
  const productsSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    'itemListElement': [
      {
        '@type': 'ListItem',
        'position': 1,
        'item': {
          '@type': 'Product',
          'name': 'Custom Android Tablets',
          'description': 'Ruggedized, lightweight Android tablets designed for portability and enterprise use. Perfect for field teams and industrial environments.',
          'url': 'https://www.truecoregadgets.com/product/tablets',
          'brand': {
            '@type': 'Brand',
            'name': 'TrueCore Gadgets'
          },
          'offers': {
            '@type': 'Offer',
            'priceCurrency': 'INR',
            'availability': 'https://schema.org/InStock'
          }
        }
      },
      {
        '@type': 'ListItem',
        'position': 2,
        'item': {
          '@type': 'Product',
          'name': 'Custom PCs India',
          'description': 'High-performance workstations built for design, business, and gaming. Maximize efficiency with hardware tailored for professional workflows.',
          'url': 'https://www.truecoregadgets.com/product/pcs',
          'brand': {
            '@type': 'Brand',
            'name': 'TrueCore Gadgets'
          },
          'offers': {
            '@type': 'Offer',
            'priceCurrency': 'INR',
            'availability': 'https://schema.org/InStock'
          }
        }
      },
      {
        '@type': 'ListItem',
        'position': 3,
        'item': {
          '@type': 'Product',
          'name': 'Desktop Computers for Business',
          'description': 'Powerful embedded systems designed for IoT applications, heavy computation, and enterprise environments requiring reliability.',
          'url': 'https://www.truecoregadgets.com/product/desktops',
          'brand': {
            '@type': 'Brand',
            'name': 'TrueCore Gadgets'
          },
          'offers': {
            '@type': 'Offer',
            'priceCurrency': 'INR',
            'availability': 'https://schema.org/InStock'
          }
        }
      }
    ]
  };

  // FAQ structured data
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': [
      {
        '@type': 'Question',
        'name': 'What types of custom electronics do you manufacture?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'TrueCore Gadgets manufactures custom Android tablets, ruggedized POS devices, enterprise desktop computers, custom PCs, and embedded IoT hardware tailored to specific business requirements.'
        }
      },
      {
        '@type': 'Question',
        'name': 'Do you provide warranty on your custom electronics?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Yes, all our products come with comprehensive warranties ranging from 1-5 years depending on the product line, with options for extended coverage and on-site service plans.'
        }
      },
      {
        '@type': 'Question',
        'name': 'Can you create custom tablets for specific industry applications?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Absolutely. We specialize in designing tablets for specific industry requirements including healthcare, logistics, manufacturing, and retail with features like barcode scanners, RFID readers, and specialized mounting options.'
        }
      },
      {
        '@type': 'Question',
        'name': 'Do you ship throughout India?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Yes, we ship to all locations throughout India with special expedited options for Mumbai, Delhi, Bangalore, Hyderabad, and other major cities.'
        }
      }
    ]
  };

  return (
    <Layout>
      <Helmet>
        <title>TrueCore Gadgets – Premium Tablets, PCs & Smart Devices in India</title>
        <meta name="description" content="TrueCore Gadgets - India's leading manufacturer of custom Android tablets, rugged devices, PCs and enterprise computers. Industry trusted brand with 25+ years experience and nationwide delivery." />
        <meta name="keywords" content="TrueCore Gadgets, custom android tablets manufacturer India, rugged industrial tablets, android POS device, embedded electronics manufacturer, IoT hardware India, enterprise mobility hardware, custom electronics for enterprises, custom PCs India, buy tablets online India, electronics manufacturer India, desktop computers for business, trusted gadget brand India, tablet manufacturers Mumbai, B2B electronics supplier India, top electronics brand 2025" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://www.truecoregadgets.com/" />
        
        {/* Open Graph Tags */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="TrueCore Gadgets – Premium Tablets, PCs & Smart Devices in India" />
        <meta property="og:description" content="TrueCore Gadgets - India's leading manufacturer of custom Android tablets, enterprise PCs, and rugged devices built for business. Shop online with nationwide delivery." />
        <meta property="og:url" content="https://www.truecoregadgets.com" />
        <meta property="og:image" content="https://www.truecoregadgets.com/lovable-uploads/461a823a-5781-4980-ad39-ffec726a88a3.png" />
        <meta property="og:locale" content="en_IN" />
        <meta property="og:site_name" content="TrueCore Gadgets" />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="TrueCore Gadgets – Premium Electronics Manufacturer India" />
        <meta name="twitter:description" content="TrueCore Gadgets - India's trusted hub for custom tablets, PCs, and enterprise hardware. Buy direct from the manufacturer with nationwide support." />
        <meta name="twitter:image" content="https://www.truecoregadgets.com/lovable-uploads/461a823a-5781-4980-ad39-ffec726a88a3.png" />
        
        {/* Additional SEO Tags */}
        <meta name="robots" content="index, follow" />
        <meta name="geo.region" content="IN-MH" />
        <meta name="geo.placename" content="Mumbai" />
        <meta name="geo.position" content="19.0760;72.8777" />
        <meta name="ICBM" content="19.0760, 72.8777" />
        
        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(productsSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>
      
      <HeroSection />
      <AboutSection />
      <ProductsSection />
      <IndustriesSection />
      <WhyChooseUsSection />
      <TestimonialsSection />
      <ContactSection />
      <CTASection />
    </Layout>
  );
};

export default Index;
